/**
 * EA VPE Management Role Service
 * Orchestrates VPE management workflows for EA (VPE Admin) role
 * Delegates business logic to feature services
 */

import { serviceFactory } from '@/services/core/serviceFactory';
import { VPEManagementService } from '@/services/features/VPEManagementService';
import type { VPE, CreateVPEData, UpdateVPEData, VPEResponse } from '@/services/features/VPEManagementService';

export class EAVPEManagementService {
  private vpeManagementService: VPEManagementService;

  constructor() {
    this.vpeManagementService = serviceFactory.getService(VPEManagementService);
  }

  /**
   * Fetch all VPEs - delegates to feature service
   */
  async fetchVPEs(): Promise<VPE[]> {
    return await this.vpeManagementService.fetchVPEs();
  }

  /**
   * Create VPE - delegates to feature service with EA-specific validation
   */
  async createVPE(vpeData: CreateVPEData): Promise<VPEResponse> {
    // EA-specific business rules can be added here
    const validation = this.vpeManagementService.validateVPEData(vpeData, false);
    
    if (!validation.isValid) {
      return {
        success: false,
        message: validation.errors.join(', ')
      };
    }

    return await this.vpeManagementService.createVPE(vpeData);
  }

  /**
   * Update VPE - delegates to feature service with EA-specific validation
   */
  async updateVPE(vpeId: number, vpeData: UpdateVPEData, originalVPE: VPE): Promise<VPEResponse> {
    // Check if there are actual changes
    if (!this.vpeManagementService.hasChanges(originalVPE, vpeData)) {
      return {
        success: false,
        message: 'No changes detected'
      };
    }

    // EA-specific business rules can be added here
    const validation = this.vpeManagementService.validateVPEData(vpeData, true);
    
    if (!validation.isValid) {
      return {
        success: false,
        message: validation.errors.join(', ')
      };
    }

    return await this.vpeManagementService.updateVPE(vpeId, vpeData);
  }

  /**
   * Delete VPE - delegates to feature service with EA-specific checks
   */
  async deleteVPE(vpeId: number): Promise<VPEResponse> {
    // EA-specific business rules for deletion can be added here
    // For example: Check if VPE has active assignments before deletion
    
    return await this.vpeManagementService.deleteVPE(vpeId);
  }

  /**
   * Validate VPE data - delegates to feature service
   */
  validateVPEData(data: Partial<CreateVPEData | UpdateVPEData>, isEditMode: boolean = false): { isValid: boolean; errors: string[] } {
    return this.vpeManagementService.validateVPEData(data, isEditMode);
  }

  /**
   * Check for changes - delegates to feature service
   */
  hasChanges(originalVPE: VPE, newData: UpdateVPEData): boolean {
    return this.vpeManagementService.hasChanges(originalVPE, newData);
  }
}

// Export singleton instance
export const eaVPEManagementService = new EAVPEManagementService();
export default eaVPEManagementService;